import moment from "moment";

class PDFReceiptReport {
    constructor() {
        this.reportData = null;
    }

    async exportToPDF(data) {
        this.reportData = data;

        await this.waitForPdfMakeReady();

        const docDefinition = this.buildDocDefinition();

        pdfMake.createPdf(docDefinition).open();
    }

    waitForPdfMakeReady() {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (typeof pdfMake !== "undefined" && pdfMake.fonts) {
                    clearInterval(interval);
                    resolve(true);
                }
            }, 100);
        });
    }

    formatDate(date) {
        return moment(date).format("DD/MM/YYYY");
    }

    formatNumber(value) {
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value || 0);
    }

    // ================= HEADER =================
    buildHeader() {
        const header = this.reportData?.header || {};

        return [
            {
                columns: [
                    {
                        width: "55%",
                        stack: [
                            {
                                text: header.company_name_en || " ",
                                fontSize: 14,
                                bold: true,
                            },
                            {
                                text: header.company_name_th || " ",
                                fontSize: 11,
                                margin: [0, 2, 0, 0],
                            },
                        ],
                    },
                    {
                        width: "45%",
                        stack: [
                            {
                                text: "CONFIDENTIAL",
                                color: "red",
                                bold: true,
                                alignment: "right",
                                fontSize: 10,
                            },
                            {
                                text: "RECEIPT REPORT",
                                bold: true,
                                alignment: "right",
                                fontSize: 14,
                                margin: [0, 3, 0, 0],
                            },
                            {
                                text: `${this.formatDate(header.date_from)} - ${this.formatDate(
                                    header.date_to
                                )}`,
                                alignment: "right",
                                fontSize: 9,
                                margin: [0, 2, 0, 0],
                            },
                        ],
                    },
                ],
                margin: [0, 0, 0, 10], // 🔥 เว้น header กับ content ให้หายใจ
            },
        ];
    }

    // ================= TABLE HEADER =================
    buildTableHeader(companyName) {
        return [
            {
                table: {
                    widths: ["*", 80, 70, 50],
                    body: [
                        [
                            {
                                text: companyName,
                                bold: true,
                                fontSize: 11,
                                margin: [0, 6, 0, 2], // 👈 ดันลงให้ baseline เท่ากัน
                                border: [false, false, false, true],
                            },
                            {
                                text: "RECEIPT NO.",
                                bold: true,
                                fontSize: 9,
                                margin: [0, 6, 0, 2], // 👈 สำคัญ ต้องเท่ากัน
                                border: [false, false, false, true],
                            },
                            {
                                text: "AMOUNT",
                                bold: true,
                                fontSize: 9,
                                margin: [0, 6, 0, 2],
                                border: [false, false, false, true],
                            },
                            {
                                text: "CUR",
                                bold: true,
                                fontSize: 9,
                                margin: [0, 6, 0, 2],
                                border: [false, false, false, true],
                            },
                        ],
                    ],
                },

                layout: {
                    hLineWidth: (i) => (i === 0 ? 0 : 1),
                    vLineWidth: () => 0,
                    hLineColor: () => "black",

                    paddingTop: () => 0,
                    paddingBottom: () => 4,
                },

                margin: [0, 20, 0, 8],
            },
        ];
    }

    // ================= CONTENT =================
    buildContent() {
        const content = this.reportData?.content || [];
        const result = [];

        content.forEach((section) => {

            // 🔥 เว้นระยะก่อนขึ้นบริษัทใหม่
            result.push({
                text: "",
                margin: [0, 30, 0, 0],
            });

            const body = [];

            // ===== HEADER ROW =====
            body.push([
                {
                    text: section.company_name,
                    bold: true,
                    fontSize: 11,
                    margin: [0, 6, 0, 2],
                    border: [false, false, false, true],
                    alignment: "left",
                },
                {
                    text: "RECEIPT NO.",
                    bold: true,
                    margin: [0, 6, 0, 2],
                    border: [false, false, false, true],
                    alignment: "left",
                },
                {
                    text: "AMOUNT",
                    bold: true,
                    margin: [0, 6, 0, 2],
                    border: [false, false, false, true],
                    alignment: "right",
                },
                {
                    text: "CUR",
                    bold: true,
                    margin: [0, 6, 0, 2],
                    border: [false, false, false, true],
                    alignment: "center",
                },
            ]);

            // ===== DATA =====
            section.dates.forEach((dateGroup) => {
                // date row
                body.push([
                    {
                        text: this.formatDate(dateGroup.date),
                        bold: true,
                        colSpan: 4,
                        margin: [0, 15, 0, 2],
                        border: [false, false, false, false],
                    },
                    {}, {}, {}
                ]);

                // items
                dateGroup.items.forEach((item) => {
                    body.push([
                        {
                            text: item.client_name || "",
                            margin: [0, 1, 0, 1],
                        },
                        {
                            text: item.receipt_no || "",
                            alignment: "left",
                            margin: [0, 1, 0, 1],
                        },
                        {
                            text: this.formatNumber(item.amount),
                            alignment: "right",
                            margin: [0, 1, 5, 1],
                        },
                        {
                            text: item.currency || "",
                            alignment: "center",
                            margin: [0, 1, 0, 1],
                        },
                    ]);
                });
            });

            // ===== TABLE =====
            result.push({
                table: {
                    // headerRows: 1,
                    // keepWithHeaderRows: 2,
                    widths: ["*", 80, 70, 50], // 🔥 ชุดเดียว = ตรงเป๊ะ
                    body: body,
                },
                layout: {
                    hLineWidth: (i) => (i === 1 ? 1 : 0), // เส้นใต้ header
                    vLineWidth: () => 0,
                },
            });
        });

        return result;
    }

    // ================= DOC =================
    buildDocDefinition() {
        return {
            pageSize: "A4",
            pageMargins: [40, 40, 40, 40], // ✅ มาตรฐานสากล

            content: [...this.buildHeader(), ...this.buildContent()],

            defaultStyle: {
                font: "Sarabun",
                fontSize: 10,
            },
        };
    }
}

export default new PDFReceiptReport();